'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ZodxOrbHero from './ZodxOrbHero';

export default function SignUpLoginScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen w-full flex" style={{ background: 'var(--background)' }}>
      {/* Left panel — brand + orb */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #060B18 0%, #0A0F1E 40%, #0D1528 100%)' }}>
        {/* Ambient background glows */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        {/* Top bar */}
        <div className="relative z-10 flex items-center gap-3 px-10 pt-10">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', boxShadow: '0 0 20px rgba(59,130,246,0.4)' }}>
            <span className="text-white font-bold text-sm font-mono">Z</span>
          </div>
          <div>
            <p className="text-base font-bold text-foreground tracking-tight">ZAX-CORP</p>
            <p className="text-xs text-muted-foreground">Powered by ZODX Intelligence</p>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-10 -mt-8">
          <ZodxOrbHero zodxState="ready" />

          <div className="text-center mt-8 max-w-sm">
            <h1 className="text-3xl font-bold text-foreground leading-tight">
              Intelligent automation.<br />
              <span className="text-gradient-blue">Transparent control.</span>
            </h1>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Deposit. Activate ZODX. Stay in control. ZODX aims to pursue risk-controlled returns — trading can result in profit or loss.
            </p>
          </div>

          {/* Stats row */}
          <div className="mt-10 flex items-center gap-6 w-full max-w-xs">
            {[
              { label: 'Users Onboarded', value: '4,218', id: 'stat-users' },
              { label: 'Strategies Active', value: '3', id: 'stat-strats' },
              { label: 'Avg. Hold Period', value: '6.4d', id: 'stat-hold' },
            ].map((s) => (
              <div key={s.id} className="flex-1 text-center">
                <p className="font-mono-nums text-base font-700 text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom disclaimer */}
        <div className="relative z-10 px-10 pb-8">
          <p className="text-xs text-muted-foreground leading-relaxed border-t border-border/50 pt-5">
            ⚠️ This is a Demo/Sandbox environment. No real money, no real trades, no real custody. ZAX-CORP is subject to applicable Indian VDA regulations. Investment involves risk. Past performance is not indicative of future results.
          </p>
        </div>
      </div>

      {/* Right panel — auth forms */}
      <div className="w-full lg:w-[48%] xl:w-[45%] flex flex-col items-center justify-center px-6 sm:px-10 py-10 overflow-y-auto scrollbar-thin">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}>
            <span className="text-white font-bold text-sm font-mono">Z</span>
          </div>
          <span className="text-base font-bold text-foreground">ZAX-CORP</span>
        </div>

        {/* Sandbox badge */}
        <div className="w-full max-w-md mb-5">
          <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-medium" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: '#93C5FD' }}>
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse-slow flex-shrink-0" />
            <span>🔐 Production Mode — Sign in with your credentials or use the demo accounts below.</span>
          </div>
        </div>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex rounded-xl p-1 mb-7" style={{ background: 'var(--muted)' }}>
            {(['login', 'register'] as const).map((tab) => (
              <button
                key={`auth-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-card-elevated text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Forms */}
          <div className="fade-in" key={activeTab}>
            {activeTab === 'login' ? (
              <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
            ) : (
              <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}